import React, { useState, useEffect, useRef } from "react";
import { TreeTable } from "primereact/treetable";
import { Column } from "primereact/column";
import apiService from "../utils/api";
import downloadIcon from "../assets/download.svg";
import "./TemplateTable.css";
import Loader from "./Loader";


const TemplatesTable = ({ onSelect, onLoaded }) => {
  const [nodes, setNodes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("");
  const [togglingId, setTogglingId] = useState(null);
  const uploadRef = useRef(null);
  const roleRaw = (sessionStorage.getItem('user_role') || '').replace(/\s+/g,'');
  const isAdmin = roleRaw === 'Admin';
  const isLegal = roleRaw === 'LegalUser' || roleRaw === 'LegalReviewer';

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await apiService.fetchData("templates");
        const templates = Array.isArray(response)
          ? response
          : response.results || [];
        const formatted = templates.map((t) => ({
          key: t.template_id?.toString(),
          data: {
            template_id: t.template_id, 
            name: t.template_name,
            type: t.template_type,
            description: t.description,
            fileUrl: t.file_url,
            is_active: t.is_active ?? 1,
            createdDate:t.created_at,
          },
        }));
        setNodes(formatted);
      } catch (error) {
        console.error("Error fetching templates:", error);
      } finally {
        setLoading(false);
        try { onLoaded && onLoaded(); } catch(_){}
      }
    };
    fetchTemplates();
  }, []);

  const handleDownload = (fileUrl) => {
    if (!fileUrl) return alert("No file available for download");
    const a = document.createElement("a");
    a.href = fileUrl;
    a.download = fileUrl.split("/").pop() || "template";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  // 👁️ Open preview
  const handlePreview = (data) => {
    if (!data.fileUrl) return alert("No file available for preview");
    setSelectedDocument(data);
    setIsModalOpen(true);
  };

  const handleDeactivate = async (row) => {
    try {
      setTogglingId(row.template_id);
      const res = await apiService.putData(`templates/${row.template_id}`, { is_active: 'false' });
      if (!res || res.status !== 'success') throw new Error(res?.message || 'Update failed');
      const updated = nodes.map(n => n.data.template_id === row.template_id ? { ...n, data: { ...n.data, is_active: 0 } } : n);
      setNodes(updated);
    } catch (e) {
      alert('Failed to deactivate');
    } finally { setTogglingId(null); }
  };

  const handleActivate = async (row) => {
    try {
      setTogglingId(row.template_id);
      const res = await apiService.putData(`templates/${row.template_id}`, { is_active: 'true' });
      if (!res || res.status !== 'success') throw new Error(res?.message || 'Update failed');
      const updated = nodes.map(n => n.data.template_id === row.template_id ? { ...n, data: { ...n.data, is_active: 1 } } : n);
      setNodes(updated);
    } catch (e) {
      alert('Failed to activate');
    } finally { setTogglingId(null); }
  };


  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  const handleModalUse = () => {
    onSelect?.(selectedDocument);
    setIsModalOpen(false);
    setSelectedDocument(null);
  };

  const filteredNodes = nodes.filter((n) => {
    const term = searchTerm.toLowerCase();
    const hay = `${n.data.name || ''} ${n.data.type || ''} ${n.data.description || ''}`.toLowerCase();
    const matchesSearch = hay.includes(term);
    const matchesType = filterType ? n.data.type === filterType : true;
    return matchesSearch && matchesType;
  });

  return (
    <div>
      <Loader isLoading={loading} message="Please wait, loading templates 🙂" />
      {!loading && (
        <>
          {/* Filters */}
          <div style={{ display: "flex", gap: "25px", marginBottom: "10px", justifyContent: "flex-end", alignItems:'center' }}>
            {/* Admin Add Template */}
            {((sessionStorage.getItem('user_role') || '').replace(/\s+/g,'') === 'Admin') && (
              <>
                <input ref={uploadRef} type="file" accept=".pdf,.doc,.docx" style={{ display:'none' }} onChange={async (e)=>{
                  const file = e.target.files && e.target.files[0];
                  if (!file) return;
                  try {
                    const form = new FormData();
                    // Append fields individually as multipart form-data
                    form.append('template_name', file.name.replace(/\.[^.]+$/,''));
                    form.append('template_type', 'MSA');
                    form.append('created_by', String(Number(sessionStorage.getItem('user_id')) || 0));
                    form.append('description', 'Uploaded from TemplatesTable');
                    form.append('file', file, file.name);
                    const res = await apiService.postData('templates', form);
                    if (!res || res.status !== 'success') throw new Error(res?.message || 'Upload failed');
                    alert('Template created successfully');
                    const refreshed = await apiService.fetchData('templates');
                    const arr = Array.isArray(refreshed) ? refreshed : (refreshed.results || []);
                    const mapped = arr.map((t)=>({ key: t.template_id?.toString(), data:{ template_id:t.template_id, name:t.template_name, type:t.template_type, description:t.description, fileUrl:t.file_url, is_active:t.is_active ?? 1, createdDate:(t.created_at||'').toString().slice(0,10) } }));
                    setNodes(mapped);
                  } catch(err){
                    console.error(err);
                    alert('Upload failed');
                  } finally {
                    e.target.value = '';
                  }
                }} />
                <button onClick={()=> uploadRef.current && uploadRef.current.click()} style={{ background:'#ff6b35', color:'#fff', border:'1px solid #ff6b35', padding:'8px 14px', borderRadius:'6px', fontWeight:600 }}>Add Template</button>
              </>
            )}

            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              style={{
                padding: "8px 12px",
                border: "1px solid #ff6b35",
                borderRadius: "6px",
                minWidth: "160px",
                outline: 'none',
                color: '#4A4A4A',
              }}
              title="Filter by template type"
            >
              <option value="">All Types</option>
              {[...new Set(nodes.map((n) => n.data.type))].map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: "260px",
                padding: "8px 12px",
                border: "1px solid #ff6b35",
                borderRadius: "6px",
                outline: 'none',
              }}
              title="Search across name, type and description"
            />
          </div>

          {/* Table */}
          <TreeTable
            value={filteredNodes}
            paginator
            rows={10}
            rowsPerPageOptions={[10,20,50]}
            showGridlines
            className="templates-table"
            paginatorClassName="custom-paginator"
          >
            {/* <Column field="name" header="Template Name" body={(node)=> (
              <a href={node.data.fileUrl} target="_blank" rel="noopener noreferrer" style={{ color:'#ff6b35', fontWeight:700, textDecoration:'none' }} title="Click to view Template">{node.data.name}</a>
            )} /> */}
            <Column
  field="name"
  header="Template Name"
  style={{ maxWidth: '250px', minWidth: '180px' }}
  body={(node) => (
    <div
      style={{
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={node.data.name}
    >
      <a
        href={node.data.fileUrl}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          color: '#ff6b35',
          fontWeight: 700,
          textDecoration: 'none',
          display: 'block',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {node.data.name}
      </a>
    </div>
  )}
/>
            <Column
  field="type"
  header="Type"
  style={{ maxWidth: '140px', minWidth: '100px' }}
  body={(node) => (
    <div
      style={{
        maxWidth: '100%',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}
      title={node.data.type}
    >
      {node.data.type}
    </div>
  )}
/>
            <Column field="description" header="Description" />
           <Column
               field="createdDate"
               header="Created"
               style={{ maxWidth: '120px', minWidth: '100px' }}
               body={(node) => {
                 const raw = node.data.createdDate;
                 let formatted = raw;
           
                 if (raw && raw.includes('-')) {
                   const [year, month, day] = raw.split('-');
                   formatted = `${day}/${month}/${year}`;
                 }
           
                 return (
                   <div
                     style={{
                       maxWidth: '100%',
                       overflow: 'hidden',
                       textOverflow: 'ellipsis',
                       whiteSpace: 'nowrap',
                     }}
                     title={formatted}
                   >
                     {formatted}
                   </div>
                 );
               }}
             />
            <Column field="is_active" header="Status" body={(node)=> (
              <span style={{ background: node.data.is_active ? '#4CAF50':'#9E9E9E', color:'#fff', padding:'2px 8px', borderRadius:'4px' }}>{node.data.is_active ? 'Active':'Inactive'}</span>
            )} />

            <Column
              header="Actions"
              body={(node) => (
                <div className="action-buttons">
      {/* Download */}
      <img
        src={downloadIcon}
        alt="Download"
        style={{ cursor: "pointer", width: "16px", height: "16px" }}
        onClick={() => handleDownload(node.data.fileUrl)}
        title="Download template"
      />

      {/* View icon hidden in table */}

      {/* ▶️ Use (Play Icon - Orange) */}
      <svg
      style={{ cursor: "pointer", width: "16px", height: "16px" }}
  onClick={() => {
  if (onSelect) {
    onSelect(node.data);   // node.data now contains template_id
  }
}}


        xmlns="http://www.w3.org/2000/svg"
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#ff6b35"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
       
      title="Use this template">
        <polygon points="5 3 19 12 5 21 5 3"></polygon>
      </svg>

      {/* {!isLegal && (
        <button
          onClick={()=> (node.data.is_active ? handleDeactivate(node.data) : handleActivate(node.data))}
          title={node.data.is_active ? 'Mark Inactive' : 'Mark Active'}
          style={{ display:'inline-flex', alignItems:'center', gap:6, border:'1px solid #ff6b35', color:'#ff6b35', background:'transparent', borderRadius:14, padding:'2px 8px', cursor:'pointer' }}
          disabled={togglingId === node.data.template_id}
        >
          {togglingId === node.data.template_id ? (
            <span className="spinner" style={{ width:12, height:12, border:'2px solid #ffe3d6', borderTopColor:'#ff6b35', borderRadius:'50%', animation:'spin 1s linear infinite' }} />
          ) : (
            <span style={{ fontWeight:600 }}>{node.data.is_active ? 'Deactivate' : 'Activate'}</span>
          )}
        </button>
      )} */}
    </div>
  )}
/>

          </TreeTable>

          {/* Modal for Preview */}
          {isModalOpen && selectedDocument && (
            <div
              style={{
                position: "fixed",
                inset: 0,
                background: "rgba(0,0,0,0.5)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <div
                style={{
                  width: "80%",
                  maxWidth: "800px",
                  background: "#fff",
                  borderRadius: "8px",
                  overflow: "hidden",
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    padding: "10px 12px",
                    background: "#ff6b35",
                    color: "#fff",
                    fontWeight: 700,
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <span>Document Preview</span>
                  <button
                    onClick={handleModalClose}
                    style={{
                      background: "none",
                      border: "none",
                      color: "#fff",
                      fontSize: "20px",
                      cursor: "pointer",
                    }}
                  >
                    ×
                  </button>
                </div>
                <iframe
                  src={selectedDocument.fileUrl}
                  title="Document Preview"
                  style={{ width: "100%", height: "75vh", border: "none" }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    padding: "10px 12px",
                    borderTop: "1px solid #e5e7eb",
                    gap: "10px",
                  }}
                >
                  <button
                    onClick={handleModalClose}
                    style={{
                      background: "transparent",
                      color: "#ff6b35",
                      border: "1px solid #ff6b35",
                      padding: "8px 14px",
                      borderRadius: "6px",
                    }}
                  >
                    Close
                  </button>
                  <button
                    onClick={handleModalUse}
                    style={{
                      background: "#ff6b35",
                      color: "#fff",
                      border: "1px solid #ff6b35",
                      padding: "8px 14px",
                      borderRadius: "6px",
                    }}
                  >
                    Use
                  </button>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default TemplatesTable;
